import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { push } from 'connected-react-router';
import i18n from 'i18next';
import _ from 'lodash';
import React, { useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Trans } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import 'react-modern-drawer/dist/index.css';

import { appActions } from '../../../modules/app';
import { companyActions } from '../../../modules/company';
import { companyFilterActions } from '../../../modules/companyFilter';
import {
  resetFilters,
  setSort,
  setItemsPerPage,
  setPage,
  setInfinityScroll,
} from '../../../modules/companyFilter/companyFilterActions';
import { eventGA } from '../../../utils/GA';
import { buildSortFilterUrl, parseSortFilterUrl } from '../../../utils/helpers/buidSortFilterUrl';
import { getCompanyRoute } from '../../../utils/helpers/routesBuilder';
import useGATracker from '../../../utils/hooks/useGATracker';
import useOnChangeLanguage from '../../../utils/hooks/useOnChangeLanguage';
import useWindowSize from '../../../utils/hooks/useWindowSize';
import { DisplayFlexS } from '../../../utils/stylesHelpers';
import { actionGA, categoryGA } from '../../constants/googleAnalytics';
import { ITEMS_PER_PAGE_CUSTOMER } from '../../constants/pagination';
import { companySorts } from '../../constants/sorts';
import { routes } from '../../routes/routesConfig';
import { LinkS, MoreItemsMessageS, SelectNew, Text, PaginationCustomer, SelectItemsPerPageCustomer } from '../common';
import { FiltersModal } from '../modals';

import CompaniesListMobile, { LoaderSection, NoCompaniesFound } from './CompaniesListMobile';
import {
  CloseIconWrapper,
  CompaniesFilterWrapS,
  CompaniesItemsWrapS,
  CompaniesWrapS,
  CompanyItemsWrap,
  CustomizeDriver,
  TextDeleteFilter,
} from './CompaniesListS';
import CompanyCard from './companyCard';
import CompaniesFilters from './filters/companiesFilters';

const { applySortParams, applyFilterParams } = companyFilterActions;

const CompanyList = ({ companiesList, push }) => (
  <>
    {companiesList.map((c) => (
      <LinkS
        to={getCompanyRoute(c._id, c.name)}
        key={c._id}
        onClick={() =>
          eventGA({
            category: categoryGA.COMPANY_LIST_PAGE,
            action: actionGA.COMPANIES,
            label: c.name,
          })
        }
      >
        <CompanyCard {...c} push={push} />
      </LinkS>
    ))}
  </>
);

const CompaniesList = () => {
  const dispatch = useDispatch();
  const location = useSelector((state) => state.router.location);
  useGATracker(location);

  const {
    companies,
    amount,
    shouldReturnAllCompaniesForKanton,
    isCompaniesFetching,
    sort,
    countPerPage,
    page,
    isInfinityScroll,
    sortParams,
    filterParams,
  } = useSelector((state) => ({
    companies: state.company.list,
    amount: state.company.amount,
    shouldReturnAllCompaniesForKanton: state.company.shouldReturnAllCompaniesForKanton,
    isCompaniesFetching: state.company.loading,
    sort: state.companyFilter.sort,
    countPerPage: state.companyFilter.countPerPage,
    page: state.companyFilter.page,
    isInfinityScroll: state.companyFilter.isInfinityScroll,
    sortParams: state.companyFilter.sortParams,
    filterParams: state.companyFilter.filterParams,
  }));

  useOnChangeLanguage();
  const { width: windowWidth } = useWindowSize();

  const [isFiltersModalOpened, setFiltersModalOpened] = useState(false);
  const [sizeLoader, setSizeLoader] = useState({ height: 0, width: 0 });
  const refLoader = useRef(null);
  const history = useHistory();

  const prevProps = useRef({ sort, page, countPerPage, isInfinityScroll });
  const currentFullPath = location.pathname + location.search;

  useEffect(() => {
    const unlistenRef = history.listen((_, action) => {
      if (action === 'POP' && location.pathname === routes.COMPANIES) {
        history.push(routes.HOME);
      }
    });
    return unlistenRef;
  }, [history]);

  useEffect(() => {
    if (location.search) {
      const { sortParams, filterParams, sort } = parseSortFilterUrl(currentFullPath);
      if (!_.isEmpty(sortParams)) dispatch(applySortParams(sortParams));
      if (!_.isEmpty(filterParams)) dispatch(applyFilterParams(filterParams));
      if (sort) dispatch(setSort(sort));
    }
    dispatch(appActions.getActiveFilters());
    dispatch(appActions.getActiveGraphFilters());
    dispatch(companyActions.fetchCompanies());
  }, []);

  useEffect(() => {
    const newUrl = buildSortFilterUrl({ sortParams, filterParams, sort });

    if (currentFullPath !== newUrl) {
      dispatch(push(newUrl));
    }
  }, [sortParams, filterParams, sort]);

  useEffect(() => {
    if (refLoader.current) {
      setSizeLoader({
        height: `${refLoader.current.offsetHeight}px`,
        width: `${refLoader.current.offsetWidth}px`,
      });
    }
  }, [companies]);

  useEffect(() => {
    if (
      countPerPage !== prevProps.current.countPerPage ||
      sort !== prevProps.current.sort ||
      isInfinityScroll !== prevProps.current.isInfinityScroll
    ) {
      dispatch(companyActions.fetchCompanies());
      dispatch(setPage(1));
    }

    if (page !== prevProps.current.page) {
      dispatch(companyActions.fetchCompanies());
    }

    prevProps.current = { sort, page, countPerPage, isInfinityScroll };
  }, [sort, page, countPerPage, isInfinityScroll]);

  return (
    <>
      {companies.length > 0 ? (
        <Helmet>
          <title>{'Beste Treuhand-, Buchhaltungs- oder Beratungsfirmen in ' + companies[0].canton}</title>
          <meta
            name="description"
            content={'Vergleichen Sie die Treuhänder, ihre Dienstleistungen und Preise in ' + companies[0].canton}
          />
          <meta
            property="og:title"
            content={'Beste Treuhand-, Buchhaltungs- oder Beratungsfirmen in ' + companies[0].canton}
          />
          <meta
            property="og:description"
            content={'Vergleichen Sie die Treuhänder, ihre Dienstleistungen und Preise in ' + companies[0].canton}
          />
          <meta property="og:url" content="https://treuhand-suche.ch/companies" />
        </Helmet>
      ) : (
        <Helmet>
          <title>General Location Company</title>
          <meta name="description" content="General Location Company" />
          <meta property="og:title" content="General Location Company" />
          <meta property="og:description" content="General Location Company" />
          <meta property="og:url" content="https://treuhand-suche.ch" />
        </Helmet>
      )}

      <CompaniesWrapS>
        <FiltersModal isModalOpen={isFiltersModalOpened} toggleModal={setFiltersModalOpened} />
        <DisplayFlexS justify="space-between" style={{ padding: '10px' }}>
          <Text style={{ fontSize: '18px' }} fontFamily="bold">
            <Trans i18nKey="companies.topCapture" />
          </Text>
        </DisplayFlexS>

        <CustomizeDriver />

        <DisplayFlexS>
          <CompaniesFilterWrapS>
            <DisplayFlexS
              justify="space-between"
              style={{ padding: '0 5px', marginBottom: 20 }}
              className="sm-display-none"
            >
              <DisplayFlexS justify="space-between" style={{ height: 36 }} aItems="center">
                <Text size="bigText">
                  <Trans i18nKey="companies.filter.capture" />
                </Text>
                <TextDeleteFilter onClick={() => dispatch(resetFilters())}>
                  <Trans i18nKey="companies.filter.clearFilterBtn" />
                  <CloseIconWrapper>
                    <FontAwesomeIcon icon="times" />
                  </CloseIconWrapper>
                </TextDeleteFilter>
              </DisplayFlexS>
            </DisplayFlexS>
            <CompaniesFilters />
          </CompaniesFilterWrapS>

          <CompaniesItemsWrapS>
            {shouldReturnAllCompaniesForKanton && <NoCompaniesFound />}

            <DisplayFlexS
              justify="flex-end"
              aItems="center"
              style={{ padding: '0 5px', marginBottom: 20 }}
              className="sm-display-none"
            >
              <DisplayFlexS width="auto" justify="flex-end" style={{ alignItems: 'center' }}>
                <Text size="bigText" style={{ marginRight: '5px' }}>
                  <Trans i18nKey="companies.selectPerPageCapture" />
                </Text>
                <SelectItemsPerPageCustomer
                  options={Object.values(ITEMS_PER_PAGE_CUSTOMER)}
                  itemsPerPage={countPerPage}
                  onChangeItemsPerPage={(changedTo) => dispatch(setItemsPerPage(changedTo))}
                  isInfinityScroll={isInfinityScroll}
                  onChangeInfinityScroll={(changedTo) => dispatch(setInfinityScroll(changedTo))}
                  setPage={(pageNumber) => dispatch(setPage(pageNumber))}
                />
                <Text size="bigText" style={{ marginRight: '5px' }}>
                  <Trans i18nKey="companies.sortCapture" />
                </Text>
                <SelectNew height="auto" style={{ width: '160px' }} values={companySorts} value={sort}>
                  {companySorts &&
                    Object.values(companySorts).map(({ value, transPath }) => (
                      <option key={value} value={value} onClick={({ target }) => dispatch(setSort(target.value))}>
                        {i18n.t(transPath)}
                      </option>
                    ))}
                </SelectNew>
              </DisplayFlexS>
            </DisplayFlexS>

            {!isInfinityScroll ? (
              <CompanyItemsWrap ref={refLoader}>
                {isCompaniesFetching && <LoaderSection sizeLoader={sizeLoader} />}
                <CompanyList companiesList={companies} push={push} />
                <PaginationCustomer
                  itemsPerPage={countPerPage}
                  itemsAmount={amount}
                  pageToRender={page}
                  setPage={(pageNumber) => dispatch(setPage(pageNumber))}
                  windowWidth={windowWidth}
                />
              </CompanyItemsWrap>
            ) : (
              <InfiniteScroll
                dataLength={companies.length}
                next={() => dispatch(setPage(page + 1))}
                hasMore={isCompaniesFetching || companies.length < amount}
                style={{ overflow: 'none' }}
                endMessage={
                  <MoreItemsMessageS>
                    <Trans i18nKey="general.paginator.noMoreItemsMessage" />
                  </MoreItemsMessageS>
                }
              >
                <CompanyItemsWrap ref={refLoader}>
                  {isCompaniesFetching && <LoaderSection sizeLoader={sizeLoader} />}
                  <CompanyList companiesList={companies} push={push} />
                </CompanyItemsWrap>
              </InfiniteScroll>
            )}
          </CompaniesItemsWrapS>
        </DisplayFlexS>
      </CompaniesWrapS>

      <CompaniesListMobile
        countPerPage={countPerPage}
        sort={sort}
        shouldReturnAllCompaniesForKanton={shouldReturnAllCompaniesForKanton}
        isCompaniesFetching={isCompaniesFetching}
        sizeLoader={sizeLoader}
        companies={companies}
        amount={amount}
        refLoader={refLoader}
      />
    </>
  );
};

export default CompaniesList;
